// frontend/src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api, { registerLogoutCallback } from '../services/apiService'; 
import { login as authLogin, register as authRegister, loginWithGoogle as authLoginWithGoogle, getUserDetails } from '../services/authService';
import { getMeuStatusLegal } from '../services/legalService';
import { getErrorMessage } from '../utils/errorHandler';
import { temModuloFinanceiro, temModuloMilhas, temModoPrestador } from '../utils/moduloAccess';
import { mesclarStatusLegal } from '../utils/legalAccess';
import { clearLocalStorageOnLogout } from '../utils/primeirosPassosStorage';
import { tryEnableWebPush } from '../pwa/webPush';
import { trackMetaEvent } from '../utils/metaPixel';

const AuthContext = createContext(null);

async function carregarUsuarioComStatusLegal() {
    const userData = await getUserDetails();
    try {
        const legal = await getMeuStatusLegal();
        return mesclarStatusLegal(userData, legal);
    } catch (e) {
        console.warn('Falha ao obter status legal; exigindo aceite por segurança.', e);
        return mesclarStatusLegal(userData, {
            aceite_ok: false,
            pendentes: userData?.legal_aceite_pendente?.length
                ? userData.legal_aceite_pendente
                : ['termos', 'privacidade'],
        });
    }
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        // Limpa sessão; preserva primeiros_passos_* e app_version
        clearLocalStorageOnLogout();
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const userData = await carregarUsuarioComStatusLegal();
            setUser(userData);
            return userData;
        } catch (e) {
            console.error('Falha ao atualizar dados do usuário', e);
            return null;
        }
    }, []);

    useEffect(() => {
        registerLogoutCallback(() => {
            logout();
        });

        const accessToken = localStorage.getItem('access_token');
        if (accessToken) {
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            
            const fetchUserDetails = async () => {
                try {
                    const userData = await carregarUsuarioComStatusLegal();
                    setUser(userData);
                    tryEnableWebPush(api);
                } catch (error) {
                    console.error("Falha na validação inicial do token:", error);
                    // Aqui não exibimos erro visual (toast) pois é um check automático
                    // mas garantimos o logout para não deixar o app em estado inconsistente
                    logout();
                } finally {
                    setLoading(false);
                }
            };
            fetchUserDetails();
        } else {
            setLoading(false);
        }
    }, [logout]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible' && localStorage.getItem('access_token')) {
                refreshUser();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [refreshUser]);

    const login = async (email, password) => {
        try {
            delete api.defaults.headers.common['Authorization'];

            const response = await authLogin(email, password);
            
            const accessToken = response.access_token || response.access;
            const refreshToken = response.refresh_token || response.refresh;
            
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('refresh_token', refreshToken);
            
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            
            const userData = await carregarUsuarioComStatusLegal();
            setUser(userData);
            tryEnableWebPush(api);
            return userData;
        } catch (error) {
            console.error("Erro no login:", error);
            logout();
            
            // 2. TRATAMENTO NO LOGIN
            // Senha certa, mas o cadastro ainda não confirmou o e-mail: a tela
            // de login oferece o reenvio do link em vez de "senha inválida".
            const codigo = error.response?.data?.codigo;
            if (String(Array.isArray(codigo) ? codigo[0] : codigo || '') === 'email_nao_verificado') {
                const erro = new Error('E-mail não confirmado');
                erro.codigo = 'email_nao_verificado';
                throw erro;
            }
            if (error.response?.status === 400 || error.response?.status === 401) {
                throw new Error("Credenciais inválidas");
            } else {
                // Filtra HTML/Erro 500 antes de jogar o erro para a tela de Login
                const cleanMessage = getErrorMessage(error, "Erro de conexão ao tentar fazer login.");
                throw new Error(cleanMessage);
            }
        }
    };

    /** Abre a sessão com tokens já emitidos (ex.: link de confirmação do e-mail). */
    const entrarComTokens = async (accessToken, refreshToken) => {
        localStorage.setItem('access_token', accessToken);
        if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        try {
            const userData = await carregarUsuarioComStatusLegal();
            setUser(userData);
            tryEnableWebPush(api);
            return userData;
        } catch (error) {
            logout();
            throw error;
        }
    };

    const loginWithGoogle = async (accessToken, ref, origem) => {
        try {
            delete api.defaults.headers.common['Authorization'];
            const response = await authLoginWithGoogle(accessToken, ref, origem);

            const accessTokenRes = response.access_token || response.access;
            const refreshToken = response.refresh_token || response.refresh;

            localStorage.setItem('access_token', accessTokenRes);
            localStorage.setItem('refresh_token', refreshToken);

            api.defaults.headers.common['Authorization'] = `Bearer ${accessTokenRes}`;

            const userData = await carregarUsuarioComStatusLegal();
            setUser(userData);
            tryEnableWebPush(api);
            // Conta criada agora pelo Google = cadastro concluído (mesmo evento do
            // cadastro por e-mail). Login de quem já tinha conta não dispara.
            if (response.novo_usuario) {
                trackMetaEvent('CompleteRegistration', { content_name: 'cadastro_google' });
            }
            return userData;
        } catch (error) {
            console.error("Erro no login com Google:", error);
            logout();
            if (error.response?.status === 400 || error.response?.status === 401) {
                throw new Error("Não foi possível autenticar com o Google. Tente novamente.");
            }
            const cleanMessage = getErrorMessage(error, "Erro ao conectar com o Google.");
            throw new Error(cleanMessage);
        }
    };

    const register = async (email, password, nome_completo, options = {}) => {
        try {
            delete api.defaults.headers.common['Authorization'];
            const payload = {
                email,
                password1: password,
                password2: password,
                nome_completo,
            };
            if (options.telefone) payload.telefone = options.telefone;
            if (options.ref) payload.ref = options.ref;
            if (options.origem) payload.origem = options.origem;
            if (options.aceite_termos_versao) payload.aceite_termos_versao = options.aceite_termos_versao;
            if (options.aceite_privacidade_versao) payload.aceite_privacidade_versao = options.aceite_privacidade_versao;
            if (options.confirmar_email) payload.confirmar_email = true;
            const response = await authRegister(payload);
            
            const accessToken = response.access_token || response.access;
            const refreshToken = response.refresh_token || response.refresh;

            if (accessToken) {
                localStorage.setItem('access_token', accessToken);
                if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
                
                api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                const userData = await carregarUsuarioComStatusLegal();
                setUser(userData);
                tryEnableWebPush(api);
                return { ok: true, user: userData };
            }
            // Cadastro com verificação de e-mail: backend retorna detail + email, sem tokens
            return { ok: true, verificationSent: true, detail: response.detail, email: response.email };
        } catch (error) {
            console.error("Erro no registro:", error);
            logout();
            
            // 3. TRATAMENTO NO REGISTRO
            // Antes retornava apenas 'false', agora lança o erro tratado
            // para que a tela de Registro possa dizer "E-mail já existe" ou "Erro no servidor"
            const cleanMessage = getErrorMessage(error, "Não foi possível realizar o cadastro.");
            const erro = new Error(cleanMessage);
            // Possível erro de digitação no e-mail: o backend sugere a correção.
            erro.emailSugerido = error?.response?.data?.email_sugerido || null;
            throw erro;
        }
    };

    const acessoPermitido = user ? Boolean(user.acesso_permitido) : false;
    const moduloFinanceiro = useMemo(() => temModuloFinanceiro(user), [user]);
    const moduloMilhas = useMemo(() => temModuloMilhas(user), [user]);
    const modoPrestador = useMemo(() => temModoPrestador(user), [user]);

    if (loading) {
        return <div style={{display:'flex', justifyContent:'center', marginTop:'50px'}}>Carregando...</div>;
    }

    return (
        <AuthContext.Provider value={{
            user,
            login,
            loginWithGoogle,
            entrarComTokens,
            logout,
            register,
            refreshUser,
            isAuthenticated: !!user,
            acessoPermitido,
            temModuloFinanceiro: moduloFinanceiro,
            temModuloMilhas: moduloMilhas,
            temModoPrestador: modoPrestador,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};